/*
 * Copyright 2017 Palantir Technologies, Inc. All rights reserved.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { __assign, __awaiter, __extends, __generator, __rest } from "tslib";
import classNames from "classnames";
import React from "react";
import { Icons, ICON_SIZE_STANDARD, ICON_SIZE_LARGE } from "@blueprintjs/icons";
import { AbstractPureComponent, Classes, DISPLAYNAME_PREFIX } from "../../common";
var Icon = /** @class */ (function (_super) {
    __extends(Icon, _super);
    function Icon() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this.state = {
            iconComponent: undefined,
        };
        // this component may have unmounted by the time iconContents load, so make sure we don't try to setState
        _this.hasUnmounted = false;
        return _this;
    }
    Icon.prototype.componentDidMount = function () {
        return __awaiter(this, void 0, void 0, function () {
            var icon;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        this.hasUnmounted = false;
                        icon = this.props.icon;
                        if (!(typeof icon === "string")) return [3 /*break*/, 2];
                        return [4 /*yield*/, this.loadIconComponentModule(icon)];
                    case 1:
                        _a.sent();
                        _a.label = 2;
                    case 2: return [2 /*return*/];
                }
            });
        });
    };
    Icon.prototype.componentDidUpdate = function (prevProps, _prevState) {
        return __awaiter(this, void 0, void 0, function () {
            var icon;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        icon = this.props.icon;
                        if (!(prevProps.icon !== icon && typeof icon === "string")) return [3 /*break*/, 2];
                        // reload the module to get the component, but it will be cached if it's the same icon
                        return [4 /*yield*/, this.loadIconComponentModule(icon)];
                    case 1:
                        // reload the module to get the component, but it will be cached if it's the same icon
                        _a.sent();
                        _a.label = 2;
                    case 2: return [2 /*return*/];
                }
            });
        });
    };
    Icon.prototype.componentWillUnmount = function () {
        this.hasUnmounted = true;
    };
    Icon.prototype.render = function () {
        var icon = this.props.icon;
        if (icon == null || typeof icon === "boolean") {
            return null;
        }
        else if (typeof icon !== "string") {
            return icon;
        }
        // strip out props we don't want rendered to the DOM
        var _a = this.props, autoLoad = _a.autoLoad, className = _a.className, color = _a.color, size = _a.size, _icon = _a.icon, intent = _a.intent, tagName = _a.tagName, _b = _a.title, title = _b === void 0 ? icon : _b, _c = _a.htmlTitle, htmlTitle = _c === void 0 ? title : _c, htmlProps = __rest(_a, ["autoLoad", "className", "color", "size", "icon", "intent", "tagName", "title", "htmlTitle"]);
        var Component = this.state.iconComponent;
        if (Component == null) {
            // fall back to icon font if unloaded or unable to load SVG implementation
            return React.createElement(tagName, __assign(__assign({}, htmlProps), { className: classNames(Classes.ICON, Classes.iconClass(icon), Classes.intentClass(intent), className), title: htmlTitle }));
        }
        else {
            return (React.createElement(Component, __assign({ className: classNames(Classes.intentClass(intent), className), color: color, size: size, tagName: tagName, title: title, htmlTitle: htmlTitle }, htmlProps)));
        }
    };
    Icon.prototype.loadIconComponentModule = function (iconName) {
        return __awaiter(this, void 0, void 0, function () {
            var iconComponent;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!(this.props.autoLoad && !this.hasUnmounted)) return [3 /*break*/, 2];
                        // if it's already been loaded, this is a no-op
                        return [4 /*yield*/, Icons.load(iconName)];
                    case 1:
                        // if it's already been loaded, this is a no-op
                        _a.sent();
                        _a.label = 2;
                    case 2:
                        iconComponent = Icons.getComponent(iconName);
                        this.setState({ iconComponent: iconComponent });
                        return [2 /*return*/];
                }
            });
        });
    };
    Icon.displayName = DISPLAYNAME_PREFIX + ".Icon";
    Icon.defaultProps = {
        autoLoad: true,
        tagName: "span",
    };
    Icon.SIZE_STANDARD = ICON_SIZE_STANDARD;
    Icon.SIZE_LARGE = ICON_SIZE_LARGE;
    return Icon;
}(AbstractPureComponent));
export { Icon };
//# sourceMappingURL=icon.js.map